import type { CSSProperties } from 'react';

import { cn } from '@/lib/cn';
import { hueFromId, initials } from '@/lib/format';

export interface AvatarProps {
  name: string;
  /** Used only to pick a stable hue. Nothing is fetched or stored. */
  id?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  className?: string;
}

const SIZES = {
  // 9px is below the type scale on purpose: two initials in a 20px circle need
  // it, and this is the one place in the app that renders text that small.
  xs: 'size-5 text-[0.5625rem]',
  sm: 'size-7 text-nano',
  md: 'size-9 text-micro',
  lg: 'size-12 text-ui',
} as const;

/**
 * Initials on a tinted disc. There are no uploaded photos in this product and
 * there is no plan for any, so this is the avatar, not a placeholder for one.
 *
 * The hue is derived from the person's id so it is stable across screens and
 * sessions; saturation and lightness come from tokens so both themes stay
 * legible without a second palette.
 */
export function Avatar({ name, id, size = 'md', className }: AvatarProps) {
  const hue = hueFromId(id ?? name);

  return (
    <span
      className={cn(
        'inline-grid shrink-0 select-none place-items-center rounded-full border font-semibold uppercase tracking-wide',
        SIZES[size],
        className,
      )}
      style={
        {
          backgroundColor: `color-mix(in srgb, hsl(${hue} 55% 50%) 16%, var(--surface))`,
          borderColor: `color-mix(in srgb, hsl(${hue} 55% 50%) 32%, transparent)`,
          color: `color-mix(in srgb, hsl(${hue} 55% 40%) 75%, var(--ink))`,
        } as CSSProperties
      }
      aria-hidden="true"
    >
      {initials(name)}
    </span>
  );
}
